/**
 * Support Chat Widget
 * Bolha flutuante + janela de conversa: cabeçalho de marca e bot de boas-vindas com
 * respostas rápidas. Conversa persistida — usuário fala com o Adm pela Central de Atendimento.
 */

import React, { useCallback, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import { Close, Send, SupportAgent } from '@mui/icons-material';
import { useManifesto } from '../../stores/manifestoStore';
import { useAuth } from '../../stores/authStore';
import { useSupportChat } from '../../hooks/useSupportChat';
import type { SupportConversationSubject, SupportFeedbackTag } from '../../services/supportChatService';

const QUICK_REPLIES: { label: string; subject: SupportConversationSubject }[] = [
  { label: 'Dúvida sobre exame', subject: 'duvida' },
  { label: 'Problema no sistema', subject: 'tecnico' },
  { label: 'Financeiro', subject: 'financeiro' },
];

const FEEDBACK_OPTIONS: { tag: SupportFeedbackTag; label: string }[] = [
  { tag: 'resolveu', label: 'Resolveu meu problema' },
  { tag: 'rapido', label: 'Atendimento rápido' },
  { tag: 'educado', label: 'Atendente educado' },
  { tag: 'demorou', label: 'Demorou muito' },
  { tag: 'nao_resolveu', label: 'Não resolveu' },
];

const WELCOME_MESSAGE = 'Oi! Sobre o que você precisa falar?';

const SupportChatWidget: React.FC = () => {
  const { manifesto } = useManifesto();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<SupportFeedbackTag[]>([]);

  // A conversa pertence a uma empresa — sem empresa ativa (ex.: perspectiva Plataforma) não
  // há onde guardá-la, então a bolha não aparece.
  const podeUsarChat = manifesto?.contextoAtivo.tipo === 'empresa';

  const { conversation, messages, isLoading, send, submitFeedback } = useSupportChat(
    podeUsarChat && open
  );

  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    const texto = input;
    // Só limpa o campo depois de confirmar o envio — senão uma falha de rede apaga o que a
    // pessoa escreveu, e ela tem que digitar tudo de novo sem nem saber que não foi.
    void send(texto).then((ok) => {
      if (ok) setInput('');
    });
  }, [input, send]);

  const handleQuickReply = useCallback(
    (label: string, subject: SupportConversationSubject) => {
      void send(label, subject);
    },
    [send]
  );

  const toggleFeedbackTag = useCallback((tag: SupportFeedbackTag) => {
    setSelectedFeedback((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }, []);

  const handleSubmitFeedback = useCallback(() => {
    if (selectedFeedback.length === 0) return;
    void submitFeedback(selectedFeedback);
    setSelectedFeedback([]);
  }, [selectedFeedback, submitFeedback]);

  if (!podeUsarChat) return null;

  const showQuickReplies = messages.length === 0;
  const showRatingPrompt =
    conversation?.status === 'resolved' && conversation.feedbackTags === null && messages.length > 0;

  return (
    <>
      <Zoom in={open}>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 24,
            width: 400,
            maxWidth: 'calc(100vw - 48px)',
            height: 620,
            maxHeight: 'calc(100vh - 140px)',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 32, height: 32 }}>
              <SupportAgent fontSize="small" />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Suporte Lidera SST
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Fale com a gente
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              aria-label="Fechar chat de suporte"
              sx={{ color: 'inherit' }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Stack
            spacing={1}
            sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5, bgcolor: 'background.default' }}
          >
            {messages.length === 0 && (
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  maxWidth: '80%',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography variant="body2">{WELCOME_MESSAGE}</Typography>
              </Box>
            )}

            {messages.map((message) => {
              const mine = String(message.senderId) === String(user?.id ?? '');
              return (
                <Box
                  key={message.id}
                  sx={{
                    alignSelf: mine ? 'flex-end' : 'flex-start',
                    maxWidth: '80%',
                    bgcolor: mine ? 'primary.main' : 'background.paper',
                    color: mine ? 'primary.contrastText' : 'text.primary',
                    border: mine ? 'none' : '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography variant="body2">{message.text}</Typography>
                </Box>
              );
            })}

            {showRatingPrompt && (
              <Box
                sx={{
                  alignSelf: 'center',
                  textAlign: 'center',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  mt: 1,
                }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Atendimento encerrado
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  O que descreve seu atendimento?
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} justifyContent="center" sx={{ mb: 1 }}>
                  {FEEDBACK_OPTIONS.map((option) => (
                    <Chip
                      key={option.tag}
                      label={option.label}
                      size="small"
                      color={selectedFeedback.includes(option.tag) ? 'primary' : 'default'}
                      variant={selectedFeedback.includes(option.tag) ? 'filled' : 'outlined'}
                      onClick={() => toggleFeedbackTag(option.tag)}
                    />
                  ))}
                </Stack>
                <Chip
                  label="Enviar avaliação"
                  size="small"
                  color="primary"
                  disabled={selectedFeedback.length === 0}
                  onClick={handleSubmitFeedback}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            )}

            {showQuickReplies && (
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {QUICK_REPLIES.map((reply) => (
                  <Chip
                    key={reply.subject}
                    label={reply.label}
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={isLoading}
                    onClick={() => handleQuickReply(reply.label, reply.subject)}
                  />
                ))}
              </Stack>
            )}
          </Stack>

          <Box
            sx={{
              p: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Escreva sua mensagem..."
              value={input}
              disabled={isLoading}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={isLoading}
              aria-label="Enviar mensagem"
            >
              <Send fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Zoom>

      <Tooltip title={open ? '' : 'Suporte'} placement="left">
        <Fab
          color="secondary"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={open ? 'Fechar chat de suporte' : 'Falar com o suporte'}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (theme) => theme.zIndex.speedDial }}
        >
          {open ? <Close /> : <SupportAgent />}
        </Fab>
      </Tooltip>
    </>
  );
};

export default SupportChatWidget;
